import React, { useState, useRef, useEffect } from 'react';
import { useSocket } from '../hooks/useSocket';
import api from '../services/api';

const s = {
  title:    { fontSize:22, fontWeight:600, color:'#f1f5f9', marginBottom:6 },
  sub:      { fontSize:13, color:'#64748b', marginBottom:24 },
  grid:     { display:'grid', gridTemplateColumns:'280px 1fr', gap:16, height:'calc(100vh - 200px)' },
  panel:    { background:'#1e293b', border:'1px solid #334155', borderRadius:12,
              display:'flex', flexDirection:'column', overflow:'hidden' },
  panelHdr: { padding:'14px 16px', borderBottom:'1px solid #334155',
              fontSize:13, fontWeight:600, color:'#94a3b8',
              textTransform:'uppercase', letterSpacing:'.5px' },
  atList:   { flex:1, overflowY:'auto' },
  atItem:   (sel) => ({
              padding:'12px 16px', cursor:'pointer', borderBottom:'1px solid #1e293b',
              background: sel ? 'rgba(14,165,233,.1)' : 'transparent',
              borderLeft: sel ? '3px solid #0ea5e9' : '3px solid transparent',
              transition:'all .15s'
            }),
  atName:   { fontSize:14, color:'#f1f5f9', fontWeight:500 },
  atInst:   { fontSize:11, color:'#64748b', marginTop:2 },
  dot:      (on) => ({ display:'inline-block', width:8, height:8, borderRadius:'50%',
                       background: on ? '#22c55e' : '#475569', marginRight:6 }),
  feed:     { flex:1, overflowY:'auto', padding:16, display:'flex',
              flexDirection:'column', gap:8 },
  msg:      (dir) => ({
              maxWidth:'75%', padding:'8px 14px', borderRadius:10, fontSize:13,
              alignSelf: dir==='out' ? 'flex-end' : 'flex-start',
              background: dir==='out' ? '#0ea5e9' : '#334155',
              color: '#f1f5f9'
            }),
  msgMeta:  (dir) => ({
              fontSize:10, color:'#64748b', marginTop:2,
              textAlign: dir==='out' ? 'right' : 'left'
            }),
  emptyFeed:{ color:'#334155', textAlign:'center', padding:40, fontSize:13 },
  flagBtn:  { background:'rgba(239,68,68,.15)', color:'#f87171', border:'1px solid rgba(239,68,68,.3)',
              borderRadius:6, padding:'2px 8px', fontSize:11, cursor:'pointer', marginLeft:6 },
  thumb:    { display:'block', maxWidth:180, maxHeight:180, borderRadius:8, border:'1px solid rgba(255,255,255,.18)', objectFit:'cover', marginBottom:6 },
  fileLink: { color:'#e0f2fe', textDecoration:'none', fontWeight:700 },
  emoji:    { fontSize:24, lineHeight:'30px' }
};

function tipoLabel(type) {
  const labels = {
    image: 'Imagem',
    video: 'Video',
    audio: 'Audio',
    document: 'Documento',
    sticker: 'Figurinha',
    reaction: 'Reacao',
    contact: 'Contato',
    location: 'Localizacao',
    other: 'Sistema'
  };
  return labels[type] || type || 'Mensagem';
}

function ConteudoMensagem({ msg }) {
  const content = msg.content || '';
  const isDataImage = msg.media_url?.startsWith('data:image/');
  const isImageUrl = msg.media_url && /\.(png|jpe?g|gif|webp)(\?|$)/i.test(msg.media_url);
  const canPreview = msg.type === 'image' && (isDataImage || isImageUrl);

  if (canPreview) {
    return (
      <div>
        <a href={msg.media_url} target="_blank" rel="noreferrer">
          <img src={msg.media_url} alt={content || 'Imagem recebida'} style={s.thumb} />
        </a>
        {content && <div>{content}</div>}
      </div>
    );
  }

  if (msg.media_url) {
    return (
      <div>
        <a href={msg.media_url} style={s.fileLink} target="_blank" rel="noreferrer">
          Abrir {tipoLabel(msg.type)}
        </a>
        {content && <div style={{ marginTop:4 }}>{content}</div>}
      </div>
    );
  }

  if (msg.type === 'reaction' && content) {
    return <span style={s.emoji}>{content}</span>;
  }

  return content || `[${tipoLabel(msg.type)}]`;
}

export default function Monitoramento() {
  const { mensagensLive, instancias, monitorarAtendente } = useSocket();
  const [atendentes, setAtendentes] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [historicoRecente, setHistoricoRecente] = useState([]);
  const feedRef = useRef(null);

  useEffect(() => {
    api.get('/atendentes').then(r => setAtendentes(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ limit: 50, ordem: 'desc' });
    if (selecionado?.id) params.set('atendente_id', selecionado.id);

    api.get(`/mensagens?${params}`).then(r => {
      const mensagens = (r.data.mensagens || []).map(m => ({
        ...m,
        atendenteId: m.atendenteId || m.atendente_id,
        ts: m.ts || m.sent_at,
      }));
      setHistoricoRecente(mensagens);
    });
  }, [selecionado]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [mensagensLive]);

  const selecionar = (at) => {
    setSelecionado(at);
    monitorarAtendente(at.id);
  };

  const mensagensCombinadas = [...mensagensLive, ...historicoRecente]
    .reduce((acc, msg) => {
      const key = msg.id || `${msg.atendenteId}-${msg.phone}-${msg.ts}-${msg.content}`;
      if (!acc.map.has(key)) {
        acc.map.set(key, true);
        acc.items.push(msg);
      }
      return acc;
    }, { map: new Map(), items: [] })
    .items
    .sort((a, b) => new Date(b.ts || b.sent_at || 0) - new Date(a.ts || a.sent_at || 0));

  const msgsFiltradas = selecionado
    ? mensagensCombinadas.filter(m => m.atendenteId === selecionado.id)
    : mensagensCombinadas;

  const flagarMsg = async (id) => {
    const motivo = prompt('Motivo para marcar esta mensagem:');
    if (motivo === null) return;
    await api.patch(`/mensagens/${id}/flag`, { flagged: true, reason: motivo });
  };

  const fmtHora = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
  };

  return (
    <div>
      <div style={s.title}>Monitoramento em tempo real</div>
      <div style={s.sub}>Mensagens chegando ao vivo — selecione um atendente para filtrar</div>

      <div style={s.grid}>
        {/* Lista de atendentes */}
        <div style={s.panel}>
          <div style={s.panelHdr}>Atendentes ({atendentes.length})</div>
          <div style={s.atList}>
            <div style={s.atItem(!selecionado)} onClick={() => setSelecionado(null)}>
              <div style={s.atName}>📡 Todos</div>
              <div style={s.atInst}>Todas as instâncias</div>
            </div>
            {atendentes.map(at => (
              <div key={at.id} style={s.atItem(selecionado?.id === at.id)}
                   onClick={() => selecionar(at)}>
                <div style={s.atName}>
                  <span style={s.dot(instancias[at.instance_name] === 'open')} />
                  {at.name}
                </div>
                <div style={s.atInst}>{at.instance_name || 'sem instância'}</div>
                <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>
                  {at.conversas_hoje || 0} conv hoje
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feed de mensagens */}
        <div style={s.panel}>
          <div style={s.panelHdr}>
            Feed de mensagens
            <span style={{ fontWeight:400, marginLeft:8, color:'#475569' }}>
              {msgsFiltradas.length} mensagens
            </span>
          </div>
          <div style={s.feed} ref={feedRef}>
            {msgsFiltradas.length === 0 && (
              <div style={s.emptyFeed}>Aguardando mensagens...</div>
            )}
            {msgsFiltradas.map((msg, i) => (
              <div key={msg.id || i} style={{ alignSelf: msg.direction==='out' ? 'flex-end':'flex-start' }}>
                <div style={s.msg(msg.direction)}>
                  <ConteudoMensagem msg={msg} />
                  <button style={s.flagBtn} onClick={() => flagarMsg(msg.id)} title="Marcar para revisão">
                    ⚑
                  </button>
                </div>
                <div style={s.msgMeta(msg.direction)}>
                  {msg.atendente_nome || ''} · {fmtHora(msg.ts || msg.sent_at)} · {msg.phone}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
